// @ts-check
import { sortArrayAlphabetically } from '@datahooks/metadata/DataUtils';

import { RAG_META, RAG_ORDER } from '../shared/businessViewConfig';

/** @typedef {import('@components/cards/shared/filters/filterUtils').FilterGroup} FilterGroup */

/**
 * Risk categories are free text from each project's workflow JSON, with no enumeration to
 * check against - so the options come from the flows on screen. A fixed list would offer
 * categories no flow has and miss any a project adds.
 *
 * @param {import('../shared/businessView.types').FlowRow[]} [flows]
 * @returns {string[]}
 */
export const collectRiskCategories = (flows = []) =>
  // Array.from rather than spreading the Set: jsconfig sets no `target`, so a spread
  // over an iterable is an error under the default it falls back to.
  sortArrayAlphabetically(Array.from(new Set(flows.flatMap((flow) => flow?.categories ?? []))));

/**
 * The filter groups for the flows table: risk level, then risk category.
 *
 * Risk levels are the three RAG bands in RAG_ORDER. 'unavailable' is deliberately not
 * offered — it is not a band, and the designs do not list it — but that does mean a
 * flow which could not be calculated is hidden whenever any risk level is ticked. See
 * the note on the table.
 *
 * @param {import('../shared/businessView.types').FlowRow[]} [flows]
 * @returns {FilterGroup[]}
 */
export const buildFlowFilterGroups = (flows = []) => [
  {
    key: 'riskLevel',
    label: 'Risk Level',
    // "At Risk" on its own reads as a value, not as a filter on risk level.
    chipPrefix: 'Risk',
    options: RAG_ORDER.map((status) => ({ value: status, label: RAG_META[status].label })),
    getValues: (flow) => (flow?.status ? [flow.status] : []),
  },
  {
    key: 'riskCategory',
    label: 'Risk Category',
    options: collectRiskCategories(flows).map((category) => ({ value: category, label: category })),
    getValues: (flow) => flow?.categories ?? [],
  },
];

export default buildFlowFilterGroups;
